import React from "react";
import PageHero from "../components/PageHero";
import SectionHead from "../components/SectionHead";
import Reveal from "../components/Reveal";
import FigureBlock from "../components/FigureBlock";

const TIMELINE = [
    {
        date: "2018",
        title: "The proof-of-concept",
        body: "NZ Police announce a facial recognition 'proof of concept' with a private technology consortium. The Privacy Commissioner is not consulted beforehand and raises concerns that no privacy impact assessment was done (Independent Police Conduct Authority, 2020).",
    },
    {
        date: "FEB–MAR 2020",
        title: "The Clearview AI trial",
        body: "Police trial the commercial search tool Clearview AI internally — approximately 180 searches, including on persons of interest in a live robbery investigation — without clearance from Police executive, the Privacy Commissioner or the Government Chief Data Steward (Radio New Zealand, 2020).",
    },
    {
        date: "MAY 2020",
        title: "Exposure and stocktake",
        body: "The trial becomes public through media reporting. Police deem the tool 'not operationally viable'; the Commissioner orders a stocktake of all emergent surveillance technologies and new internal approval policy follows (Radio New Zealand, 2020).",
    },
    {
        date: "NOV 2020",
        title: "IPCA finds the gap",
        body: "The Independent Police Conduct Authority concludes its investigation: police adopted emergent technologies ahead of any governance framework, and a central governance group for such technologies is established (Independent Police Conduct Authority, 2020).",
    },
    {
        date: "AUG 2024",
        title: "First FRT policy",
        body: "NZ Police issue their first internal facial recognition policy — banning live FRT on camera feeds ('the risks currently outweigh the benefits') and permitting only approved, lawful-access, retrospective use. Crucially, oversight remains internal: police regulate police (Radio New Zealand, 2024).",
    },
    {
        date: "DEC 2024 – MAR 2025",
        title: "A code is drafted",
        body: "The Office of the Privacy Commissioner consults publicly on a Biometric Processing Privacy Code that would, for the first time, set dedicated legal rules for biometric technologies — replacing the Privacy Act's 13 general information privacy principles for biometric processing (Office of the Privacy Commissioner, 2024).",
    },
    {
        date: "JUN 2025",
        title: "The amber light",
        body: "The Privacy Commissioner reports on Foodstuffs' supermarket FRT trial: 225,972,004 faces scanned, two misidentifications at a 90% confidence threshold, systems trained on overseas datasets that may under-serve Māori and Pacific faces. The verdict is explicitly 'an amber light, not a green light' (Office of the Privacy Commissioner, 2025).",
    },
];

export default function NzContext() {
    return (
        <div>
            <PageHero
                fileNo="FR-NZ-03"
                lines={["The New Zealand", <>context — <i className="text-amber">a lag</i></>]}
                lede="New Zealand faces a severe legislative lag on biometric profiling: no dedicated statute governs facial recognition, so deployment has repeatedly run ahead of governance. The Privacy Act 2020 supplies general information privacy principles — but it was not written for population-scale biometric surveillance."
                meta={["SUBJECT: LAW, POLICY, HISTORY"]}
            />

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead
                        no="3.1"
                        kicker="Timeline"
                        title="Seven moments that define the gap"
                        lede="What follows is the documented record — assembled from the IPCA investigation, Privacy Commissioner reports, and contemporary journalism (all cited on the References page)."
                    />
                    <ol className="relative border-l border-white/15 ml-3 sm:ml-6">
                        {TIMELINE.map((t, i) => (
                            <Reveal key={t.date} delay={i * 0.05}>
                                <li className="relative pl-8 sm:pl-12 pb-10 last:pb-0" data-testid={`nz-timeline-item-${i + 1}`}>
                                    <span className="absolute -left-[7px] top-1.5 w-3.5 h-3.5 rounded-full border-2 border-amber bg-ink" />
                                    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber mb-2">{t.date}</p>
                                    <h3 className="font-display text-xl sm:text-2xl text-paper mb-3">{t.title}</h3>
                                    <p className="text-sm sm:text-base leading-relaxed text-ash max-w-3xl">{t.body}</p>
                                </li>
                            </Reveal>
                        ))}
                    </ol>
                </div>
            </section>

            <section className="bg-sheet border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    <div>
                        <SectionHead
                            no="3.2"
                            kicker="The legal position"
                            title="What law actually applies right now?"
                        />
                        <div className="space-y-6">
                            {[
                                {
                                    t: "Privacy Act 2020",
                                    b: "New Zealand's general privacy statute. Its 13 information privacy principles apply to biometric data, but they were drafted for forms and filing cabinets, not continuous biometric capture in public space. There is no requirement for public consultation before a police FRT deployment.",
                                },
                                {
                                    t: "New Zealand Bill of Rights Act 1990",
                                    b: "Section 21 protects against unreasonable search; sections 13–14 protect expression and peaceful assembly. Mass scanning of crowds engages these rights even though no court in NZ has yet ruled on police FRT directly.",
                                },
                                {
                                    t: "Internal police policy (2024)",
                                    b: "The strongest current rule on police FRT is the police's own policy. It bans live use and requires approval — but as internal self-regulation it can be rewritten by the same organisation it binds (Radio New Zealand, 2024).",
                                },
                                {
                                    t: "The Biometric Processing Privacy Code (draft)",
                                    b: "The OPC's draft code would add a proportionality test — organisations must show biometric use is necessary and effective — plus transparency duties and mandatory safeguards (Office of the Privacy Commissioner, 2024). Its final form will define New Zealand's position for years.",
                                },
                            ].map((row, i) => (
                                <Reveal key={row.t} delay={i * 0.06}>
                                    <div className="border border-white/10 p-6 hover:border-amber/40 transition-colors" data-testid={`nz-law-card-${i + 1}`}>
                                        <h3 className="font-mono text-sm text-amber mb-2">{row.t}</h3>
                                        <p className="text-sm leading-relaxed text-ash">{row.b}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                    <div className="lg:pt-4">
                        <FigureBlock
                            num="5"
                            src="https://images.unsplash.com/photo-1559659964-7f369983b418?q=80&w=1600&auto=format&fit=crop"
                            alt="Surveillance cameras mounted on a brick wall above a street sign"
                            caption="Public-space cameras operate on every New Zealand high street. Unlike comparable jurisdictions, no dedicated statute tells their operators — including Police — where recognition may point them."
                            ratio="4/3"
                        />
                        <Reveal delay={0.12} className="mt-10">
                            <blockquote className="border-l-2 border-amber pl-6">
                                <p className="font-display italic text-xl sm:text-2xl text-paper leading-snug">
                                    “An inherently intrusive technology” — the Privacy Commissioner's description of FRT,
                                    even where its use is justified and compliant (Office of the Privacy Commissioner, 2025).
                                </p>
                            </blockquote>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
